import { AuthEnum } from '@/services/permission.service';
import { KeyObject } from '@/common/interface';
import axios from 'axios';
import { message } from 'ant-design-vue';
import { BASE_URL } from '@/common/constant/index';

axios.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  (response) => {
    if (response.status === 200 && response.data && response.data.data) {
      return Promise.resolve(response.data.data as KeyObject<boolean>);
    } else {
      return null;
    }
  },
  (error) => {
    return processError(error);
  },
);


/**
 * 获取权限（优化版）
 * 策略：优先读取缓存，仅对缺失或过期的权限发起网络请求，实现增量更新
 */
const asyncPermission = async (pnames: AuthEnum[]): Promise<KeyObject<boolean> | null> => {
  if (!pnames.length) return {};

  const permissionStorage = localStorage.getItem('permission');
  const cachedMap = permissionStorage ? JSON.parse(permissionStorage) : {};
  const result: KeyObject<boolean> = {};
  const needFetch: AuthEnum[] = []; // 需要发起请求的权限列表

  const nowSeconds = Math.floor(Date.now() / 1000);

  // 1. 分离：区分“有效缓存”和“需要请求”的权限
  pnames.forEach((pname) => {
    const cachedItem = cachedMap[pname];

    // 检查缓存是否存在且未过期
    if (cachedItem && cachedItem.expired > nowSeconds) {
      result[pname] = cachedItem.value;
    } else {
      needFetch.push(pname);
    }
  });

  // 2. 如果所有权限都有有效缓存，直接返回
  if (needFetch.length === 0) {
    return result;
  }

  try {
    // 3. 仅请求缺失的权限
    const fetchedPermissions = await getAsyncPermission(needFetch);

    if (fetchedPermissions) {
      // 4. 合并结果：将新请求的数据合并到结果中
      Object.assign(result, fetchedPermissions);

      // 5. 更新缓存
      // 注意：这里建议保存完整的权限状态（包括刚才命中的缓存和新的请求结果）
      // 如果 getAsyncPermission 返回的是完整对象，也可以直接覆盖，但增量保存更稳妥
      savePermission(result);
    }

    return result;
  } catch (error) {
    // console.error('获取权限失败', error);
    // 即使请求失败，如果有部分缓存，可以选择返回部分缓存，或者返回 null
    return null;
  }
};

const getAsyncPermission = async (pnames: AuthEnum[]): Promise<KeyObject<boolean> | null> => {
  try {
    const userStorage = localStorage.getItem('user');
    const userId = userStorage
      ? JSON.parse(userStorage).id
      : null;
    const AUTHORIZATION = userStorage
      ? JSON.parse(userStorage).token
      : null;
    if (!userId || !AUTHORIZATION) {
      message.error('无用户信息，权限请求失败！');
      return null;
    }

    const res = (await axios.post(`${BASE_URL}/rbac/permission/list`, {
      permission_name: pnames.join(','),
    }, {
      headers: {
        AUTHORIZATION,
      },
    })) as unknown as KeyObject<boolean>;

    if (res) {
      return res;
    } else {
      return {};
    }
  } catch (error: any) {
    if (error.response.status === 401) {
      message.error('登录已过期，请重新登录！');
      return { status: false };
    } else {
      message.error('权限请求失败！');
      return null;
    }
  }
};

const savePermission = (permission: KeyObject<boolean | null>) => {
  const permissionMap = new Map();
  const permissionStorage = localStorage.getItem('permission');

  if (permissionStorage) {
    for (const [key, val] of Object.entries(JSON.parse(permissionStorage))) {
      permissionMap.set(key, val);
    }
  }

  const entries = Object.entries(permission);
  for (const [key, val] of entries) {
    permissionMap.set(key, {
      value: val,
      expired: Math.floor(Date.now() / 1000) + 60 * 60 * 24,
    });
  }

  localStorage.setItem('permission', JSON.stringify(Object.fromEntries(permissionMap)));
};

const processError = (error: any) => {
  if (axios.isCancel(error)) {
    // eslint-disable-next-line no-console
    // console.log(`取消重复请求${error.message}`);
  } else {
    // 其它异常处理
    return Promise.reject(error);
  }
};

export default asyncPermission;
